import { useMemo } from "react";
import { getEmailFormatError } from "../../Utils/family-tree/email";
import { getPhoneInputValue, phoneInputCountrySearchProps } from "../../Utils/phone";
import { AddMemberAutoInput, calcYearsDeceased } from "./descendantSheetHelpers";
import { AddMemberYearField } from "./AddMemberYearField";
import {
  ADD_MEMBER_DATE_FORMAT,
  ADD_MEMBER_FIELD_LIMITS,
} from "../../Utils/family-tree/addMemberFieldLimits";
import {
  getSpouseFallbackDob,
  isEditModeMinor,
} from "../../Utils/family-tree/memberAge";
import { isInRelationshipMaritalStatus } from "../../Utils/family-tree/dropdown";

export function MainSpouseSection ({
    handleFileChange,
    DropdownSelect,
    sortedGenderOptions,
    sortedMaritalStatusOptions,
    sortedCellTypeOptions,
    grandmotherData,
    handleGrandmotherChange,
    setSpouseValidationErrors,
    spouseValidationErrors,
    touchedFields,
    formSubmitted,
    setTouchedFields,
    grandfatherData,
    CustomDatePicker,
    dateValidationErrors,
    sortedStates,
    sortedLivingDeceasedOptions,
    emailCheckTimeoutRef,
    checkEmailExists,
    handlePhoneChange,
    PhoneInput,
    sortedDivorceOptions,
    showSpouseSection,
    toggleSpouseSection,
    openSpouseSections,
    uploadedImages,
    removeImage,
    spouseImageValidationError,
    setSpouses,
    isEditMode = false,
    editMemberData = null,
}){

    const patchMainSpouse = (partial) => {
        setSpouses((prev) => {
            const next = [...prev];
            if (!next[0]) return prev;
            next[0] = { ...next[0], ...partial };
            return next;
        });
    };

    const yearsDeceased = useMemo(
        () => calcYearsDeceased(grandmotherData.dod),
        [grandmotherData.dod]
    );

    const primaryMaritalLabel =
        sortedMaritalStatusOptions?.find(
            (o) => o.value === String(grandfatherData.maritalStatus ?? "")
        )?.label ?? "";

    const spouseGender = grandmotherData?.gender || grandmotherData?.spouseGender;
    const isSpouseDeceased = grandmotherData.aliveStatus === "2";
    const isContactDisabled = isSpouseDeceased;
    const isSpouseDivorced = grandmotherData.divorce == 1;
    const showSpouseMaritalBands =
        grandfatherData.maritalStatus != 1;
    const skipMaritalFieldRequirements = [3, 4].includes(
        Number(grandfatherData.maritalStatus)
    );
    const isInRelationship = isInRelationshipMaritalStatus(
        grandfatherData.maritalStatus
    );

    const renderSpousePhoneField = (narrow = false) => (
        <div
            className={`form-group addmember-form-group${
                narrow ? " addmember-field-narrow-match-divorced" : ""
            }`}
        >
            <label>Country Code &amp; Cell Phone #</label>
            <PhoneInput
                country={"us"}
                value={getPhoneInputValue(
                    grandmotherData.phone,
                    grandmotherData.countryCode
                )}
                placeholder=""
                {...phoneInputCountrySearchProps}
                onChange={(value, country) => handlePhoneChange(value, country, true)}
                disabled={isContactDisabled}
                inputStyle={{
                    width: "100%",
                    height: "47px",
                    paddingLeft: "48px",
                    backgroundColor: isContactDisabled ? "#f5f5f5" : "white",
                    opacity: isContactDisabled ? 0.6 : 1,
                    cursor: isContactDisabled ? "not-allowed" : "text",
                }}
                containerStyle={{ marginBottom: "0", width: "100%", maxWidth: "100%" }}
                inputProps={{ name: "spouse_phone", required: false }}
            />
        </div>
    );

    const spouseFallbackDob = useMemo(
        () => getSpouseFallbackDob(editMemberData),
        [editMemberData]
    );
    const isSpouseMinor = useMemo(
        () =>
            isEditModeMinor({
                isEditMode,
                dob: grandmotherData.dob,
                currentAge: grandmotherData.currentAge,
                fallbackDob: spouseFallbackDob,
            }),
        [
            isEditMode,
            grandmotherData.dob,
            grandmotherData.currentAge,
            spouseFallbackDob,
        ]
    );

    const renderSpouseEmailField = () => {
        return (
            <div id="spouse-email-field" className="form-group addmember-form-group">
                <label>Email</label>
                <input
                    type="email"
                    name="email"
                    value={grandmotherData.email || ""}
                    placeholder={
                        grandmotherData.aliveStatus === "2"
                            ? "Gray entire row if spouse is deceased"
                            : "name@example.com"
                    }
                    onChange={(e) => {
                        const nextEmail = e.target.value;
                        patchMainSpouse({ email: nextEmail });
                        setSpouseValidationErrors((prev) => ({ ...prev, email: "" }));
                        if (emailCheckTimeoutRef.current) {
                            clearTimeout(emailCheckTimeoutRef.current);
                        }
                        const trimmed = nextEmail.trim();
                        if (trimmed) {
                            emailCheckTimeoutRef.current = setTimeout(() => {
                                const formatError = getEmailFormatError(trimmed);
                                if (formatError) {
                                    setSpouseValidationErrors((prev) => ({
                                        ...prev,
                                        email: formatError,
                                    }));
                                    return;
                                }
                                if (grandmotherData.aliveStatus === "1") {
                                    checkEmailExists(trimmed, "spouse");
                                }
                            }, 500);
                        }
                    }}
                    onBlur={(e) => {
                        if (emailCheckTimeoutRef.current) {
                            clearTimeout(emailCheckTimeoutRef.current);
                        }
                        const trimmed = e.target.value.trim();
                        if (trimmed !== e.target.value) {
                            patchMainSpouse({ email: trimmed });
                        }
                        const formatError = getEmailFormatError(trimmed);
                        if (formatError) {
                            setSpouseValidationErrors((prev) => ({
                                ...prev,
                                email: formatError,
                            }));
                        } else if (trimmed && grandmotherData.aliveStatus === "1") {
                            checkEmailExists(trimmed, "spouse");
                        }
                        setTouchedFields((prev) => ({
                            ...prev,
                            spouse: { ...prev.spouse, email: true },
                        }));
                    }}
                    disabled={grandmotherData.aliveStatus === "2"}
                    className="form-control"
                    style={{
                        width: "100%",
                        boxSizing: "border-box",
                        height: "47px",
                        minHeight: "47px",
                    }}
                />
                {spouseValidationErrors.email &&
                    (touchedFields.spouse.email || formSubmitted) && (
                        <div
                            style={{
                                color: "#d32f2f",
                                fontSize: "12px",
                                marginTop: "4px",
                            }}
                        >
                            {spouseValidationErrors.email}
                        </div>
                    )}
            </div>
        );
    };

    const renderSpouseNameField = (field, label, isRequired, showLabel = true) => {
        const isMaidenNameDisabled =
            field === "maidenName" && (spouseGender === "1" || spouseGender === 1);
        const isSuffixDisabled =
            field === "suffix" && (spouseGender === "2" || spouseGender === 2);
        const isFieldDisabled = isMaidenNameDisabled || isSuffixDisabled;

        return (
            <div
                className={showLabel ? "form-group addmember-form-group" : "addmember-spouse-name-cell"}
                key={field}
            >
                {showLabel && (
                    <label
                        htmlFor={`grandmother-${field}`}
                        className={isRequired ? "required-label" : ""}
                    >
                        {label}
                    </label>
                )}
                <input
                    id={`grandmother-${field}`}
                    type="text"
                    name={field}
                    autoComplete={field === "suffix" ? "off" : undefined}
                    value={
                        field === "suffix"
                            ? (() => {
                                  const suffixValue = grandmotherData[field] || "";
                                  if (suffixValue === "") return "";
                                  const cleanedValue = suffixValue.replace(/\./g, "");
                                  return cleanedValue + ".";
                              })()
                            : grandmotherData[field]
                    }
                    onChange={(e) => {
                        if (field === "suffix") {
                            let inputValue = e.target.value.replace(/\./g, "");
                            handleGrandmotherChange({
                                target: { name: field, value: inputValue },
                            });
                        } else {
                            handleGrandmotherChange(e);
                        }
                        if (e.target.value.trim() && isRequired) {
                            setSpouseValidationErrors((prev) => ({
                                ...prev,
                                [field]: "",
                            }));
                        }
                    }}
                    onBlur={(e) => {
                        let trimmedValue = e.target.value.trim();
                        if (field === "suffix") {
                            trimmedValue = trimmedValue.replace(/\./g, "");
                        }
                        if (
                            trimmedValue !==
                            (field === "suffix" ? grandmotherData[field] : e.target.value)
                        ) {
                            handleGrandmotherChange({
                                target: { name: field, value: trimmedValue },
                            });
                        }
                        if (isRequired) {
                            setTouchedFields((prev) => ({
                                ...prev,
                                spouse: { ...prev.spouse, [field]: true },
                            }));
                        }
                    }}
                    required={isRequired}
                    disabled={isFieldDisabled}
                    placeholder={
                        field === "maidenName" && isMaidenNameDisabled
                            ? ""
                            : undefined
                    }
                    style={
                        isFieldDisabled
                            ? {
                                  backgroundColor: "#f5f5f5",
                                  opacity: 0.6,
                                  cursor: "not-allowed",
                              }
                            : {}
                    }
                />
                {isRequired &&
                    spouseValidationErrors[field] &&
                    (touchedFields.spouse[field] || formSubmitted) && (
                        <div
                            style={{
                                color: "#d32f2f",
                                fontSize: "12px",
                                marginTop: "4px",
                            }}
                        >
                            {spouseValidationErrors[field]}
                        </div>
                    )}
            </div>
        );
    };

    return (
        <>
                    {/* Main Spouse Section - Always shown when married */}
                    {showSpouseSection && (
              <div className="section spouse-section addmember-section">
                
                
                <div
                  className="section-header addmember-section-header"
                  onClick={() => toggleSpouseSection("main-spouse")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && toggleSpouseSection("main-spouse")
                  }
                >
                  <h3>Spouse Data</h3>
                  <span
                    className={`dropdown-icon ${openSpouseSections.includes("main-spouse") ? "rotated" : ""
                      }`}
                  >
                    ▼
                  </span>
                </div>

                {openSpouseSections.includes("main-spouse") && (
                  <>
                    {/* ... (Spouse Image upload JSX) ... */}
                    <div
                      id="spouse-main-image-section"
                      className="image-row addmember-image-row"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {/* Default avatar when no spouse images are uploaded */}
                      {!uploadedImages.some(
                        (img) => img.type == "spouse-main-spouse"
                      ) && (
                          <div
                            style={{
                              width: "65px",
                              height: "65px",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={window.getPublicUrl("/assets/images/d-user.jpg")}
                              alt="Spouse"
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}

                      {/* Display uploaded spouse images */}
                      {uploadedImages
                        .filter((img) => img.type == "spouse-main-spouse")
                        .map((img, index) => (
                          <div
                            key={img.id}
                            style={{
                              width: "65px",
                              height: "65px",
                              // borderRadius: '4px',
                              // This makes first image circular

                              overflow: "hidden",
                              position: "relative",
                              // border: '1px solid #E3E3E3',

                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={img.url}
                              alt={`Spouse Upload ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",

                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(img.id);
                              }}
                              style={{
                                position: "absolute",
                                top: "2px",
                                right: "2px",
                                background: "rgba(0,0,0,0.5)",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "18px",
                                height: "18px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: "12px",
                                padding: 0,
                                lineHeight: 1,
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}

                      {/* Plus button - show if less than 2 total images (main + spouse) */}
                      {uploadedImages.filter((img) => img.type == "spouse")
                        .length < 2 && (
                          <div
                            // onClick={() => fileInputRef.current?.click()}
                            onClick={() =>
                              document
                                .getElementById("spouse-main-image-input")
                                ?.click()
                            }
                            style={{
                              cursor: "pointer",
                              width: "65px",
                              height: "65px",
                              borderRadius: "4px",
                              border: "1px dashed #ccc",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#f5f5f5",
                              flexShrink: 0,
                            }}
                          >
                            <span style={{ fontSize: "24px", color: "#666" }}>
                              +
                            </span>
                            <input
                              id="spouse-main-image-input"
                              // ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleFileChange(e, "spouse-main-spouse")
                              }
                              multiple
                            />
                          </div>
                        )}
                    </div>
                    {spouseImageValidationError && (
                      <div
                        style={{
                          color: "#ff4d4f",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        Spouse image is required .
                      </div>
                    )}

                    <div className="row form-grid addmember-form-grid addmember-spouse-form-grid">
                    <div className="addmember-spouse-band addmember-spouse-band--names">
                      <div className="form-group addmember-form-group addmember-field-gender">
                        <label className="required-label" id="gender-field-spouse">Gender</label>
                        <DropdownSelect
                          options={sortedGenderOptions}
                          value={spouseGender}
                          onChange={(val) => {
                            handleGrandmotherChange({
                              target: { name: "gender", value: val },
                            });
                            if (val) {
                              setSpouseValidationErrors((prev) => ({
                                ...prev,
                                gender: "",
                              }));
                            }
                          }}
                          maxMenuHeight={180}
                          focusNextOnSelect
                          maxSearchLength={ADD_MEMBER_FIELD_LIMITS.GENDER}
                        />
                        {spouseValidationErrors.gender &&
                          (touchedFields.spouse.gender || formSubmitted) && (
                            <div
                              style={{
                                color: "#d32f2f",
                                fontSize: "12px",
                                marginTop: "4px",
                              }}
                            >
                              {spouseValidationErrors.gender}
                            </div>
                          )}
                      </div>

                      <div className="addmember-spouse-names-combined">
                        {renderSpouseNameField("firstName", "First Name", true)}
                        {renderSpouseNameField("lastName", "Last Name", true)}
                      </div>

                      {renderSpouseNameField("suffix", "Suffix", false)}
                      {renderSpouseNameField("maidenName", "Maiden Name", false)}
                    </div>

                      <div className="addmember-spouse-band addmember-spouse-band--lifecycle">
                        <div id="spouse-alive-status-field" className="form-group addmember-form-group">
                          <label className="required-label">Living / Deceased</label>
                          <DropdownSelect
                            options={sortedLivingDeceasedOptions}
                            value={grandmotherData.aliveStatus}
                            onChange={(val) => {
                              handleGrandmotherChange({
                                target: { name: "aliveStatus", value: val },
                              });
                              if (val) {
                                setSpouseValidationErrors((prev) => ({
                                  ...prev,
                                  aliveStatus: "",
                                }));
                              }
                              if (val === "2") {
                                setSpouses((prev) => {
                                  const newSpouses = [...prev];
                                  if (newSpouses[0]) {
                                    newSpouses[0] = {
                                      ...newSpouses[0],
                                      email: "",
                                      email_domain: "",
                                      email_custom_domain: "",
                                    };
                                  }
                                  return newSpouses;
                                });
                                setSpouseValidationErrors((prev) => ({
                                  ...prev,
                                  email: "",
                                }));
                              }
                            }}
                            onBlur={() => {
                              setTouchedFields((prev) => ({
                                ...prev,
                                spouse: { ...prev.spouse, aliveStatus: true },
                              }));
                            }}
                            placeholder="Select"
                            maxMenuHeight={95}
                          />
                          {spouseValidationErrors.aliveStatus &&
                            (touchedFields.spouse.aliveStatus || formSubmitted) && (
                              <div
                                style={{
                                  color: "#d32f2f",
                                  fontSize: "12px",
                                  marginTop: "4px",
                                }}
                              >
                                {spouseValidationErrors.aliveStatus}
                              </div>
                            )}
                        </div>

                        <div className="form-group addmember-form-group">
                          <label className={isSpouseMinor ? "" : "required-label"}>
                            Date of Birth
                          </label>
                          <CustomDatePicker
                            value={grandmotherData.dob}
                            onChange={handleGrandmotherChange}
                            name="dob"
                            format={ADD_MEMBER_DATE_FORMAT}
                            autoFocusNextOnComplete
                            required={!isSpouseMinor}
                            error={
                              !!dateValidationErrors.spouse.dob &&
                              (touchedFields.spouse.dob || formSubmitted)
                            }
                            helperText={
                              touchedFields.spouse.dob || formSubmitted
                                ? dateValidationErrors.spouse.dob
                                : ""
                            }
                          />
                        </div>

                        <div className="form-group addmember-form-group">
                          <label>Date of Death</label>
                          <CustomDatePicker
                            value={grandmotherData.dod || ""}
                            onChange={handleGrandmotherChange}
                            name="dod"
                            format={ADD_MEMBER_DATE_FORMAT}
                            autoFocusNextOnComplete
                            required={grandmotherData.aliveStatus == "2"}
                            disabled={grandmotherData.aliveStatus !== "2"}
                            error={
                              !!dateValidationErrors.spouse.dod &&
                              (touchedFields.spouse.dod || formSubmitted)
                            }
                            helperText={
                              touchedFields.spouse.dod || formSubmitted
                                ? dateValidationErrors.spouse.dod
                                : ""
                            }
                          />
                        </div>

                        {grandmotherData.aliveStatus == "2" ||
                        grandmotherData.ageOfDeath ? (
                          <AddMemberAutoInput
                            label="Age at Time of Death"
                            name="ageOfDeath"
                            value={grandmotherData.ageOfDeath}
                          />
                        ) : (
                          <AddMemberAutoInput
                            label="Current Age"
                            name="currentAge"
                            value={grandmotherData.currentAge}
                          />
                        )}

                        <AddMemberAutoInput
                          label="# of Years Deceased"
                          name="yearsDeceased"
                          value={yearsDeceased}
                        />
                      </div>

                      {showSpouseMaritalBands && (
                        <>
                          <div className="addmember-spouse-band addmember-spouse-band--marital">
                            <div className="form-group addmember-form-group addmember-field-auto addmember-field-marital-status">
                              <label>Marital Status</label>
                              <input
                                type="text"
                                readOnly
                                value={primaryMaritalLabel}
                                className="addmember-auto-input"
                              />
                            </div>

                            <div
                              id="spouse-divorce-field"
                              className="form-group addmember-form-group addmember-field-divorced"
                            >
                              <label
                                className={
                                  grandmotherData.aliveStatus == 2 ||
                                  skipMaritalFieldRequirements
                                    ? ""
                                    : "required-label"
                                }
                              >
                                Divorced?
                              </label>
                              <DropdownSelect
                                options={sortedDivorceOptions}
                                value={isInRelationship ? "" : grandmotherData.divorce}
                                onChange={(val) => {
                                  handleGrandmotherChange({
                                    target: { name: "divorce", value: val },
                                  });
                                  if (val) {
                                    setSpouseValidationErrors((prev) => ({
                                      ...prev,
                                      divorce: "",
                                    }));
                                  }
                                }}
                                onBlur={() => {
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    spouse: { ...prev.spouse, divorce: true },
                                  }));
                                }}
                                placeholder="Select"
                                maxMenuHeight={88}
                                disabled={isInRelationship}
                              />
                              {grandmotherData.aliveStatus != 2 &&
                                !skipMaritalFieldRequirements &&
                                spouseValidationErrors.divorce &&
                                (touchedFields.spouse.divorce || formSubmitted) && (
                                  <div
                                    style={{
                                      color: "#d32f2f",
                                      fontSize: "12px",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {spouseValidationErrors.divorce}
                                  </div>
                                )}
                            </div>

                            {grandmotherData.divorce == 1 && !isInRelationship && (
                              <div
                                id="spouse-year-divorced-field"
                                className="form-group addmember-form-group"
                              >
                                <label>Year Divorced</label>
                                <AddMemberYearField
                                  value={grandmotherData.divorce_date}
                                  onChange={handleGrandmotherChange}
                                  name="divorce_date"
                                  disabled={isInRelationship}
                                />
                                {(touchedFields?.spouse?.divorce_date || formSubmitted) &&
                                  dateValidationErrors?.spouse?.divorce_date && (
                                    <div
                                      style={{
                                        color: "#d32f2f",
                                        fontSize: "12px",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {dateValidationErrors.spouse.divorce_date}
                                    </div>
                                  )}
                              </div>
                            )}

                            <div className="form-group addmember-form-group">
                              <label
                                className={
                                  skipMaritalFieldRequirements ? "" : "required-label"
                                }
                              >
                                Wedding Anniversary
                              </label>
                              <CustomDatePicker
                                value={
                                  isInRelationship
                                    ? ""
                                    : grandmotherData?.weddingAnniversary || ""
                                }
                                onChange={handleGrandmotherChange}
                                name="weddingAnniversary"
                                format={ADD_MEMBER_DATE_FORMAT}
                                autoFocusNextOnComplete
                                required={!skipMaritalFieldRequirements}
                                disabled={isInRelationship}
                                error={
                                  !!dateValidationErrors.spouse.weddingAnniversary &&
                                  (touchedFields.spouse.weddingAnniversary || formSubmitted)
                                }
                                helperText={
                                  touchedFields.spouse.weddingAnniversary || formSubmitted
                                    ? dateValidationErrors.spouse.weddingAnniversary
                                    : ""
                                }
                              />
                            </div>

                            {grandmotherData.divorce != 1 && (
                              <AddMemberAutoInput
                                label="Time Married"
                                name="marriedYears"
                                value={grandmotherData.marriedYears}
                              />
                            )}
                          </div>

                        </>
                      )}

                      {showSpouseMaritalBands && isSpouseDivorced ? (
                          <div
                            className={`addmember-spouse-band addmember-spouse-band--divorced-contact${
                              isContactDisabled
                                ? " addmember-spouse-contact-deceased"
                                : ""
                            }`}
                          >
                            <div className="form-group addmember-form-group">
                              <label>Divorce Date</label>
                              <CustomDatePicker
                                value={grandmotherData.divorce_date}
                                onChange={handleGrandmotherChange}
                                name="divorce_date"
                                format={ADD_MEMBER_DATE_FORMAT}
                                autoFocusNextOnComplete
                                required={false}
                                error={
                                  !!dateValidationErrors?.spouse?.divorce_date &&
                                  (touchedFields?.spouse?.divorce_date || formSubmitted)
                                }
                                helperText={
                                  touchedFields?.spouse?.divorce_date || formSubmitted
                                    ? dateValidationErrors?.spouse?.divorce_date
                                    : ""
                                }
                              />
                            </div>
                            <div className="form-group addmember-form-group addmember-field-auto addmember-field-time-married">
                              <label>Time Married</label>
                              <input
                                type="text"
                                name="marriedYears"
                                value={grandmotherData.marriedYears || ""}
                                readOnly
                                className="addmember-auto-input"
                              />
                            </div>
                            {renderSpousePhoneField(true)}
                            <div className="form-group addmember-form-group addmember-field-narrow-match-divorced">
                              <label>Cell Type</label>
                              <DropdownSelect
                                options={sortedCellTypeOptions}
                                value={grandmotherData.phone_type}
                                onChange={(val) =>
                                  handleGrandmotherChange({
                                    target: { name: "phone_type", value: val },
                                  })
                                }
                                placeholder=""
                                maxMenuHeight={88}
                                disabled={isContactDisabled}
                              />
                            </div>
                            {renderSpouseEmailField()}
                          </div>
                      ) : (
                        <div
                          className={`addmember-spouse-band addmember-spouse-band--contact${
                            isContactDisabled ? " addmember-spouse-contact-deceased" : ""
                          }`}
                        >
                          <div className="form-group addmember-form-group addmember-field-narrow-match-divorced">
                            <label>Cell Type</label>
                            <DropdownSelect
                              options={sortedCellTypeOptions}
                              value={grandmotherData.phone_type}
                              onChange={(val) =>
                                handleGrandmotherChange({
                                  target: { name: "phone_type", value: val },
                                })
                              }
                              placeholder="Select cell type"
                              maxMenuHeight={88}
                              disabled={isContactDisabled}
                            />
                          </div>
                          {renderSpousePhoneField(false)}
                          {renderSpouseEmailField()}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
        </>
    )
}